<script setup>
import { t } from '../../stores/i18n'
/**
 * One field for picking named things — tags on a transaction, the category it
 * belongs to — searched rather than laid out as a wall of chips. Past a handful
 * of them, every option on screen at once is noise and the one you want is
 * quicker to type than to find.
 *
 * The chips inside the field are only what this edit actually touches; the list
 * opens underneath on focus and narrows as you type, and a name that is not on
 * it yet is offered anyway, so picking never means leaving the form first.
 *
 * The list sits in the flow rather than floating: a sheet body scrolls, and an
 * absolutely-positioned menu would be clipped by it.
 */
import { computed, nextTick, ref } from 'vue'
import Icon from './Icon.vue'

const props = defineProps({
  /** Ties the outer `<label for>` to the text field. */
  id: { type: String, required: true },
  /** Everything on offer, as `{ id, name }`. */
  options: { type: Array, default: () => [] },
  /**
   * How one option reads right now:
   * `{ tone: 'off' | 'on' | 'part' | 'gone', note?: string, tail?: string }`.
   * Anything but `off` earns a chip in the field.
   */
  look: { type: Function, default: () => ({ tone: 'off' }) },
  placeholder: { type: String, default: '' },
  /**
   * One at a time: the field reads as an ordinary select — the choice shown as
   * plain text rather than a chip, since a chip is how you say "one of several"
   * — and picking replaces what is there and closes the list.
   */
  single: { type: Boolean, default: false },
  /** In `single` mode, the id of the chosen option. */
  value: { type: String, default: '' },
  /** What the offer to use an unlisted name says. */
  createLabel: { type: String, default: '' },
})

const emit = defineEmits(['pick', 'create'])

const query = ref('')
const open = ref(false)
const cursor = ref(0)
const root = ref(null)
const input = ref(null)

const read = (option) => ({ option, ...props.look(option.id) })

/** The chips: what a multi-select has gathered so far, and nothing in single. */
const chosen = computed(() =>
  props.single ? [] : props.options.map(read).filter((row) => row.tone !== 'off'),
)

/** The one choice, in single mode — by name, for the field to show. */
const chosenName = computed(
  () => props.options.find((o) => o.id === props.value)?.name ?? (props.single ? props.value : ''),
)

/**
 * Closed, the field shows the choice like any other select; open, it shows what
 * is being typed and keeps the choice as the placeholder behind it.
 */
const text = computed(() => (props.single && !open.value ? chosenName.value : query.value))

const matches = computed(() => {
  const term = query.value.trim().toLowerCase()
  return props.options.filter((o) => !term || o.name.toLowerCase().includes(term)).map(read)
})

/** A name that is not on the list is worth offering anyway. */
const newName = computed(() => {
  const name = query.value.trim()
  if (!name) return ''
  return props.options.some((o) => o.name.toLowerCase() === name.toLowerCase()) ? '' : name
})

const rows = computed(() => matches.value.length + (newName.value ? 1 : 0))
const atCreate = computed(() => !!newName.value && cursor.value === matches.value.length)

const TONE = {
  on: { chip: 'chip-on', icon: 'check' },
  part: { chip: '!border-dashed !border-accent/50 !text-accent', icon: null },
  gone: { chip: '!border-danger/40 !bg-danger/12 !text-danger line-through', icon: 'minus' },
}
const toneOf = (tone) => TONE[tone] ?? { chip: '', icon: null }

const list = ref(null)

const show = () => {
  // Opening a single select clears the box to type in; the choice stays in the
  // placeholder and ticked in the list, so nothing is lost by it.
  if (props.single) query.value = ''
  open.value = true
  cursor.value = 0
  // The sheet body scrolls, and the field may be sitting at the bottom of it:
  // a list that opens out of sight is no better than no list.
  nextTick(() => list.value?.scrollIntoView({ block: 'nearest' }))
}
const focus = () => input.value?.focus()
const toggle = () => {
  if (open.value) return (open.value = false)
  reopen()
}

/**
 * Clicking the field opens the list. It cannot rely on the input's own focus
 * event: after a single pick the input still holds the focus, so focusing it
 * again fires nothing.
 */
const reopen = () => {
  show()
  nextTick(focus)
}

/**
 * One at a time means the list has nothing left to offer after a pick. Several
 * at a time keeps the field, for the next one.
 *
 * `tapped`: the pick was a tap on a touch screen. After a single pick there is
 * nothing left to type, so the on-screen keyboard is let go of rather than
 * left covering the sheet. A keyboard user on a computer keeps the focus where
 * it was, as Enter always does.
 */
const settle = (tapped = false) => {
  query.value = ''
  cursor.value = 0
  if (props.single) {
    open.value = false
    if (tapped) input.value?.blur()
  } else nextTick(focus)
}

const touch = () => !!globalThis.matchMedia?.('(pointer: coarse)').matches

function pick(id, tapped = false) {
  emit('pick', id)
  settle(tapped)
}

function create(tapped = false) {
  if (!newName.value) return
  emit('create', newName.value)
  settle(tapped)
}

function commit() {
  if (!open.value) return show()
  if (atCreate.value) return create()
  const row = matches.value[cursor.value]
  if (row) pick(row.option.id)
  else if (newName.value) create()
}

const move = (delta) => {
  if (!open.value) return show()
  if (!rows.value) return
  cursor.value = (cursor.value + delta + rows.value) % rows.value
}

/**
 * Escape belongs to the list while it is open, and to the sheet the moment it
 * is not — swallowing it either way would trap someone in the form.
 */
function onEsc(event) {
  if (!open.value) return
  event.stopPropagation()
  open.value = false
}

/**
 * Closes only when the focus has left the control *and* its list — the list is
 * a sibling of the field, so checking the field alone would shut it on the way
 * to clicking a row.
 *
 * That alone is not enough on an iPhone: Safari never focuses a button it is
 * tapped on, so the field loses the focus to nothing, this closes the list,
 * and the tap's click lands on a row that is no longer there. So the list does
 * not take the focus at all (`mousedown.prevent` below): the field keeps it,
 * nothing blurs, and the click reaches the row. Scrolling the list by touch is
 * not a mousedown and is untouched.
 */
const onFocusOut = (event) => {
  if (!root.value?.contains(event.relatedTarget)) open.value = false
}
</script>

<template>
  <div ref="root" @focusout="onFocusOut">
    <!-- The chips live inside the field, so it reads as one control. `.input`
         styles its own focus, which a div never gets: the ring comes from
         whatever inside it has the focus instead. -->
    <div
      class="input flex cursor-text flex-wrap items-center gap-1.5 !py-2
             focus-within:border-accent/60 focus-within:ring-2 focus-within:ring-accent/25"
      @click="reopen"
    >
      <button
        v-for="row in chosen"
        :key="row.option.id"
        type="button"
        class="chip !py-1 !text-[12.5px]"
        :class="toneOf(row.tone).chip"
        :data-choice="row.option.name"
        :data-tone="row.tone"
        :aria-label="`${row.option.name}, ${row.note ?? t('picker.chosen')}`"
        @click.stop="pick(row.option.id)"
      >
        <Icon v-if="toneOf(row.tone).icon" :name="toneOf(row.tone).icon" :size="12" />
        {{ row.option.name }}
        <span v-if="row.tail" class="tnum text-[11px] opacity-70">{{ row.tail }}</span>
      </button>

      <input
        :id="id"
        ref="input"
        class="min-w-[7rem] flex-1 bg-transparent text-[15px] outline-none placeholder:text-faint"
        maxlength="40"
        autocomplete="off"
        role="combobox"
        :value="text"
        :aria-expanded="open"
        :aria-controls="`${id}-list`"
        :placeholder="single && chosenName ? chosenName : chosen.length ? t('picker.addAnother') : (placeholder || t('picker.search'))"
        @input="query = $event.target.value"
        @focus="show"
        @keydown.enter.prevent="commit"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.esc="onEsc"
      />

      <button
        v-if="single && chosenName"
        type="button"
        class="btn-ghost shrink-0 !px-1.5 !py-1"
        :aria-label="t('picker.clear', { name: chosenName })"
        @click.stop="pick(value)"
      >
        <Icon name="x" :size="15" />
      </button>
      <button
        type="button"
        class="btn-ghost shrink-0 !px-1.5 !py-1"
        :aria-label="open ? t('picker.hide') : t('picker.show')"
        @click.stop="toggle"
      >
        <Icon name="down" :size="16" class="transition" :class="open && 'rotate-180'" />
      </button>
    </div>

    <div
      v-if="open"
      :id="`${id}-list`"
      ref="list"
      class="scroll-thin mt-1.5 max-h-56 overflow-y-auto rounded-xl border border-line bg-surface
             p-1.5 shadow-[var(--c-shadow)]"
      role="listbox"
      data-picker-list
      @mousedown.prevent
    >
      <button
        v-for="(row, i) in matches"
        :key="row.option.id"
        type="button"
        class="menu-row"
        :class="[i === cursor && 'bg-raised', row.tone === 'gone' && 'text-danger']"
        role="option"
        :data-choice="row.option.name"
        :data-tone="row.tone"
        :aria-selected="row.tone === 'on' || row.tone === 'part'"
        @mousemove="cursor = i"
        @click="pick(row.option.id, touch())"
      >
        <span class="flex min-w-0 items-center gap-1.5">
          <Icon
            v-if="toneOf(row.tone).icon"
            :name="toneOf(row.tone).icon"
            :size="13"
            :class="row.tone === 'gone' ? 'text-danger' : 'text-accent'"
          />
          <span class="truncate" :class="row.tone === 'gone' && 'line-through'">{{ row.option.name }}</span>
        </span>
        <span v-if="row.note" class="shrink-0 text-[11.5px] text-faint">{{ row.note }}</span>
      </button>

      <button
        v-if="newName"
        type="button"
        class="menu-row text-accent"
        :class="atCreate && 'bg-raised'"
        role="option"
        :aria-selected="false"
        @mousemove="cursor = matches.length"
        @click="create(touch())"
      >
        <span class="flex items-center gap-1.5">
          <Icon name="plus" :size="13" /> {{ createLabel || t('picker.make') }} “{{ newName }}”
        </span>
      </button>

      <p v-if="!rows" class="px-2 py-1.5 text-[12.5px] text-faint">
        {{ options.length ? t('picker.noMatch') : t('picker.emptyList') }}
      </p>
    </div>
  </div>
</template>
